import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import api, { errMsg, formatTL } from "@/lib/api";
import PageHeader from "@/components/app/PageHeader";
import ExportButtons from "@/components/app/ExportButtons";
import { toast } from "sonner";
import {
  Plus, Trash2, Save, Loader2, ChevronDown, Pencil, X, CalendarCheck2,
  Wand2, Eraser, Copy, Factory, Download, FileSpreadsheet
} from "lucide-react";

const TR_MONTHS = ["Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran", "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"];
const monthTitle = (m) => (m ? `${TR_MONTHS[parseInt(m.slice(5, 7), 10) - 1]} ${m.slice(0, 4)}` : "");
const VAT_OPTIONS = [0, 10, 20];

// Gün başlıkları: hafta sonu gölgeli
const dayMeta = (month, n) => {
  const [y, m] = month.split("-").map(Number);
  return Array.from({ length: n }, (_, i) => {
    const dow = new Date(y, m - 1, i + 1).getDay();
    return { day: i + 1, weekend: dow === 0 || dow === 6 };
  });
};

// Export kolonları: Servis, Giriş, 1..N, T, Fiyat, Tutar
const buildExportColumns = (n, priceLabel) => [
  { key: "name", label: "Servis" },
  { key: "start_time", label: "Giriş" },
  ...Array.from({ length: n }, (_, i) => ({ key: `d${i + 1}`, label: String(i + 1) })),
  { key: "total", label: "T", format: "number" },
  { key: "price", label: priceLabel, format: "money" },
  { key: "amount", label: "Tutar", format: "money" },
];

const rowToExport = (name, start, days, total, price, amount) => ({
  name, start_time: start || "",
  ...Object.fromEntries(days.map((v, i) => [`d${i + 1}`, v || ""])),
  total, price, amount,
});

// ── Servis Tanımları paneli ──
const ServiceDefs = ({ company, month, suppliers, defs, onChanged }) => {
  const [open, setOpen] = useState(defs.length === 0);
  const [form, setForm] = useState(null); // null | {id?, name, vehicle_plate, start_time, supplier_id, sale_price, buy_price}
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => { if (defs.length === 0) setOpen(true); }, [defs.length]);

  const blank = { name: "", vehicle_plate: "", start_time: "", supplier_id: "", sale_price: "", buy_price: "" };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Servis adı girin"); return; }
    setSaving(true);
    try {
      const payload = {
        company_name: company, name: form.name, vehicle_plate: form.vehicle_plate,
        start_time: form.start_time, supplier_id: form.supplier_id,
        sale_price: parseFloat(form.sale_price) || 0, buy_price: parseFloat(form.buy_price) || 0,
      };
      if (form.id) await api.put(`/service-defs/${form.id}`, payload);
      else await api.post("/service-defs", payload);
      toast.success(form.id ? "Servis güncellendi" : "Servis eklendi");
      setForm(null);
      onChanged();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };

  const del = async (d) => {
    if (!window.confirm(`"${d.name}" servisi ve tüm puantajları silinsin mi?`)) return;
    try { await api.delete(`/service-defs/${d.id}`); toast.success("Silindi"); onChanged(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const importRoutes = async () => {
    try {
      const { data } = await api.post("/service-defs/import-routes", { company_name: company });
      toast.success(`${data.created} servis güzergahlardan içe aktarıldı — fiyatları girmeyi unutmayın`);
      onChanged();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const uploadExcel = async (file) => {
    if (!file) return;
    const withDays = window.confirm(
      "Excel'deki GÜNLER (puantaj) de aktarılsın mı?\n\nTamam = Fiyatlar + puantaj günleri\nİptal = Sadece servis fiyatları"
    );
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("company_name", company);
      fd.append("month", month);
      fd.append("import_days", withDays ? "true" : "false");
      const { data } = await api.post("/service-defs/import-excel", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(
        `Excel içe aktarıldı: ${data.created} yeni servis, ${data.updated} fiyat güncellendi` +
        (data.days_imported ? ` · ${data.days_imported} servisin puantajı ${data.excel_month || data.month} dönemine işlendi` : ""),
        { duration: 8000 }
      );
      if (data.excel_month && data.excel_month !== month) {
        toast.info(`Dikkat: Excel'deki dönem ${data.excel_month} — puantaj o aya işlendi, ay seçiciden görüntüleyebilirsiniz`, { duration: 9000 });
      }
      onChanged();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const supName = (id) => suppliers.find(s => s.id === id)?.name || "";
  const inp = "px-2 py-1.5 bg-white border border-[#E5E5E5] focus:border-[#0020C2] focus:outline-none text-xs w-full";

  return (
    <div className="bg-white border border-[#E5E5E5] rounded-md mb-4" data-testid="service-defs-panel">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-5 py-3 text-left">
        <div className="flex items-center gap-2">
          <Factory className="w-4 h-4 text-[#0020C2]" />
          <span className="text-[11px] uppercase tracking-widest font-bold">Servis Tanımları ({defs.length})</span>
          <span className="text-[11px] text-neutral-400">— servis adı, araç, tedarikçi, satış/alış fiyatı</span>
        </div>
        <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-5 pb-4">
          <table className="w-full text-xs border border-[#E5E5E5]">
            <thead className="bg-[#FAFAFA]">
              <tr>
                {["Servis Adı", "Araç / Plaka", "Giriş", "Tedarikçi (Cari)", "Satış ₺ (Firma)", "Alış ₺ (Tedarikçi)", ""].map((h, i) => (
                  <th key={i} className={`px-2 py-2 text-[10px] uppercase tracking-widest text-neutral-500 font-semibold ${i >= 4 && i <= 5 ? "text-right" : "text-left"}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {defs.map((d) => (
                <tr key={d.id} className="border-t border-[#F5F5F5]">
                  <td className="px-2 py-1.5 font-semibold">{d.name}</td>
                  <td className="px-2 py-1.5 num">{d.vehicle_plate || "-"}</td>
                  <td className="px-2 py-1.5 num">{d.start_time || "-"}</td>
                  <td className="px-2 py-1.5">{supName(d.supplier_id) || <span className="text-neutral-400">-</span>}</td>
                  <td className="px-2 py-1.5 text-right num font-semibold">{formatTL(d.sale_price)}</td>
                  <td className="px-2 py-1.5 text-right num">{formatTL(d.buy_price)}</td>
                  <td className="px-2 py-1.5 text-right whitespace-nowrap">
                    <button data-testid={`svc-edit-${d.id}`} onClick={() => setForm({ ...d, sale_price: String(d.sale_price || ""), buy_price: String(d.buy_price || "") })} className="p-1 hover:bg-[#F5F5F5]"><Pencil className="w-3 h-3" /></button>
                    <button data-testid={`svc-del-${d.id}`} onClick={() => del(d)} className="p-1 hover:bg-[#DC2626] hover:text-white"><Trash2 className="w-3 h-3" /></button>
                  </td>
                </tr>
              ))}
              {defs.length === 0 && <tr><td colSpan={7} className="px-3 py-4 text-center text-neutral-400">Henüz servis tanımı yok — ekleyin veya güzergahlardan içe aktarın.</td></tr>}
            </tbody>
          </table>

          {form ? (
            <div className="mt-3 border border-[#0020C2] bg-[#F5F8FF] p-3 grid grid-cols-2 md:grid-cols-7 gap-2 items-end" data-testid="svc-form">
              <div className="md:col-span-2">
                <div className="text-[9px] uppercase tracking-widest text-neutral-500 font-semibold">Servis Adı *</div>
                <input data-testid="svc-form-name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="örn: DARICA SERVİSİ" className={inp} />
              </div>
              <div>
                <div className="text-[9px] uppercase tracking-widest text-neutral-500 font-semibold">Araç / Plaka</div>
                <input data-testid="svc-form-plate" value={form.vehicle_plate} onChange={e => setForm({ ...form, vehicle_plate: e.target.value })} placeholder="34 ABC 123" className={`${inp} num`} />
              </div>
              <div>
                <div className="text-[9px] uppercase tracking-widest text-neutral-500 font-semibold">Giriş Saati</div>
                <input data-testid="svc-form-time" value={form.start_time} onChange={e => setForm({ ...form, start_time: e.target.value })} placeholder="08:00" className={`${inp} num`} />
              </div>
              <div>
                <div className="text-[9px] uppercase tracking-widest text-neutral-500 font-semibold">Tedarikçi</div>
                <select data-testid="svc-form-supplier" value={form.supplier_id} onChange={e => setForm({ ...form, supplier_id: e.target.value })} className={inp}>
                  <option value="">— Yok (öz mal) —</option>
                  {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div>
                <div className="text-[9px] uppercase tracking-widest text-neutral-500 font-semibold">Satış ₺/gün</div>
                <input data-testid="svc-form-sale" type="number" step="0.01" value={form.sale_price} onChange={e => setForm({ ...form, sale_price: e.target.value })} className={`${inp} num text-right`} />
              </div>
              <div>
                <div className="text-[9px] uppercase tracking-widest text-neutral-500 font-semibold">Alış ₺/gün</div>
                <input data-testid="svc-form-buy" type="number" step="0.01" value={form.buy_price} onChange={e => setForm({ ...form, buy_price: e.target.value })} className={`${inp} num text-right`} />
              </div>
              <div className="col-span-2 md:col-span-7 flex justify-end gap-2">
                <button onClick={() => setForm(null)} className="px-3 py-1.5 text-[10px] uppercase tracking-widest font-semibold border border-[#E5E5E5] hover:bg-white">Vazgeç</button>
                <button data-testid="svc-form-save" onClick={save} disabled={saving} className="flex items-center gap-1.5 px-4 py-1.5 text-[10px] uppercase tracking-widest font-bold bg-[#0020C2] text-white hover:bg-[#0A0A0A] disabled:opacity-50">
                  {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} {form.id ? "Güncelle" : "Ekle"}
                </button>
              </div>
            </div>
          ) : (
            <div className="mt-3 flex items-center gap-2 flex-wrap">
              <button data-testid="svc-add-btn" onClick={() => setForm(blank)} className="flex items-center gap-1.5 px-3 py-2 text-[10px] uppercase tracking-widest font-bold bg-[#0A0A0A] text-white hover:bg-[#0020C2]">
                <Plus className="w-3.5 h-3.5" /> Servis Ekle
              </button>
              <button data-testid="svc-import-btn" onClick={importRoutes} className="flex items-center gap-1.5 px-3 py-2 text-[10px] uppercase tracking-widest font-bold border border-[#0020C2] text-[#0020C2] hover:bg-[#0020C2] hover:text-white">
                <Download className="w-3.5 h-3.5" /> Güzergahlardan İçe Aktar
              </button>
              <button
                data-testid="svc-excel-btn"
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                title="SATOS 'Firma Hakedişi' formatındaki Excel'den toplu fiyat (ve istenirse puantaj) yükleyin"
                className="flex items-center gap-1.5 px-3 py-2 text-[10px] uppercase tracking-widest font-bold border border-[#16A34A] text-[#16A34A] hover:bg-[#16A34A] hover:text-white disabled:opacity-50"
              >
                {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileSpreadsheet className="w-3.5 h-3.5" />}
                {uploading ? "Yükleniyor..." : "Excel'den Fiyat Yükle"}
              </button>
              <input ref={fileRef} type="file" accept=".xlsx" className="hidden" data-testid="svc-excel-input" onChange={(e) => uploadExcel(e.target.files?.[0])} />
              <span className="text-[10px] text-neutral-400">Excel formatı: Servis | Giriş | 1-31 günler | Fiyat (SATOS hakediş çıktısı)</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ── Tedarikçi hakediş özeti ──
const SupplierSummary = ({ month, refreshKey }) => {
  const [data, setData] = useState(null);
  const [dedForm, setDedForm] = useState({}); // {supplier_id: {description, amount}}

  const load = useCallback(async () => {
    try { const { data } = await api.get("/puantaj/supplier-summary", { params: { month } }); setData(data); }
    catch { /* sessiz */ }
  }, [month]);
  useEffect(() => { load(); }, [load, refreshKey]);

  const addDed = async (sid) => {
    const f = dedForm[sid] || {};
    if (!f.description?.trim() || !parseFloat(f.amount)) { toast.error("Kesinti açıklaması ve tutarı girin"); return; }
    try {
      await api.post("/puantaj/deductions", { supplier_id: sid, month, description: f.description.trim(), amount: parseFloat(f.amount) });
      toast.success("Kesinti eklendi — tedarikçi hakedişi güncellendi");
      setDedForm(d => ({ ...d, [sid]: { description: "", amount: "" } }));
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const delDed = async (id) => {
    try { await api.delete(`/puantaj/deductions/${id}`); toast.success("Kesinti silindi"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  if (!data || data.suppliers.length === 0) return null;
  const n = data.days_in_month;

  return (
    <div className="bg-white border border-[#E5E5E5] rounded-md mb-6" data-testid="supplier-summary">
      <div className="px-5 py-4 border-b border-[#E5E5E5]">
        <div className="text-[11px] uppercase tracking-[0.15em] text-neutral-500 font-semibold">Tedarikçi Hakedişleri</div>
        <div className="text-lg font-bold font-heading mt-0.5">{monthTitle(month)} — Cari Bazlı ({data.suppliers.length} tedarikçi)</div>
      </div>
      <div className="divide-y divide-[#F5F5F5]">
        {data.suppliers.map((s) => (
          <div key={s.supplier_id} className="px-5 py-4" data-testid={`supplier-card-${s.supplier_id}`}>
            <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
              <div>
                <div className="font-bold text-sm">{s.supplier_name}</div>
                <div className="text-[11px] text-neutral-500 num">{s.phone}{s.iban ? ` · ${s.iban}` : ""}</div>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-right">
                  <div className="text-[9px] uppercase tracking-widest text-neutral-500">Hakediş − Kesinti = Net</div>
                  <div className="num text-sm">
                    {formatTL(s.total_amount)} <span className="text-[#DC2626]">− {formatTL(s.deduction_total)}</span> = <b className="text-[#0020C2]">{formatTL(s.net)}</b>
                  </div>
                </div>
                <ExportButtons
                  variant="compact"
                  testidPrefix={`sup-export-${s.supplier_id}`}
                  filename={`tedarikci_hakedis_${s.supplier_name.toLocaleLowerCase("tr-TR").replace(/[^a-z0-9]/g, "_")}_${month}`}
                  title={`TEDARİKÇİ HAKEDİŞİ — ${s.supplier_name}`}
                  subtitle={`Dönem: ${monthTitle(month)}${s.phone ? ` · Tel: ${s.phone}` : ""}${s.iban ? ` · IBAN: ${s.iban}` : ""} · Hakediş: ${formatTL(s.total_amount)} · Kesinti: ${formatTL(s.deduction_total)} · NET: ${formatTL(s.net)}`}
                  sections={[
                    {
                      name: "Puantaj", heading: "Günlük Puantaj",
                      columns: [
                        { key: "company_name", label: "Firma" },
                        { key: "service", label: "Güzergah" },
                        { key: "vehicle_plate", label: "Araç" },
                        ...Array.from({ length: n }, (_, i) => ({ key: `d${i + 1}`, label: String(i + 1) })),
                        { key: "total", label: "T", format: "number" },
                        { key: "buy_price", label: "Fiyat", format: "money" },
                        { key: "amount", label: "Hakediş", format: "money" },
                      ],
                      rows: [
                        ...s.rows.map(r => ({
                          company_name: r.company_name, service: r.service, vehicle_plate: r.vehicle_plate,
                          ...Object.fromEntries(r.days.map((v, i) => [`d${i + 1}`, v || ""])),
                          total: r.total, buy_price: r.buy_price, amount: r.amount,
                        })),
                        { company_name: "TOPLAM", service: "", vehicle_plate: "", total: s.rows.reduce((a, r) => a + r.total, 0), buy_price: "", amount: s.total_amount },
                      ],
                    },
                    ...(s.deductions.length > 0 ? [{
                      name: "Kesintiler", heading: "Kesintiler",
                      columns: [
                        { key: "description", label: "Açıklama" },
                        { key: "amount", label: "Tutar", format: "money" },
                      ],
                      rows: [...s.deductions, { description: "KESİNTİ TOPLAMI", amount: s.deduction_total }, { description: "NET ÖDENECEK", amount: s.net }],
                    }] : []),
                  ]}
                />
              </div>
            </div>
            <table className="w-full text-xs border border-[#F5F5F5]">
              <tbody>
                {s.rows.map((r, i) => (
                  <tr key={i} className="border-t border-[#F5F5F5]">
                    <td className="px-2 py-1">{r.company_name}</td>
                    <td className="px-2 py-1 font-semibold">{r.service}</td>
                    <td className="px-2 py-1 num">{r.vehicle_plate}</td>
                    <td className="px-2 py-1 text-right num"><b>{r.total}</b> gün</td>
                    <td className="px-2 py-1 text-right num">{formatTL(r.buy_price)}</td>
                    <td className="px-2 py-1 text-right num font-bold">{formatTL(r.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {/* Kesintiler */}
            <div className="mt-2 flex items-start gap-4 flex-wrap">
              <div className="flex-1 min-w-[260px]">
                {s.deductions.map((d) => (
                  <div key={d.id} className="flex items-center gap-2 text-xs py-0.5">
                    <span className="text-[#DC2626] num">− {formatTL(d.amount)}</span>
                    <span className="flex-1 truncate">{d.description}</span>
                    <button data-testid={`ded-del-${d.id}`} onClick={() => delDed(d.id)} className="p-0.5 hover:bg-[#DC2626] hover:text-white"><X className="w-3 h-3" /></button>
                  </div>
                ))}
                <div className="flex items-center gap-1.5 mt-1">
                  <input
                    data-testid={`ded-desc-${s.supplier_id}`}
                    value={dedForm[s.supplier_id]?.description || ""}
                    onChange={e => setDedForm(d => ({ ...d, [s.supplier_id]: { ...d[s.supplier_id], description: e.target.value } }))}
                    placeholder="Kesinti açıklaması (örn: yakıt avansı)"
                    className="flex-1 px-2 py-1 border border-[#E5E5E5] text-xs"
                  />
                  <input
                    data-testid={`ded-amount-${s.supplier_id}`}
                    type="number" step="0.01"
                    value={dedForm[s.supplier_id]?.amount || ""}
                    onChange={e => setDedForm(d => ({ ...d, [s.supplier_id]: { ...d[s.supplier_id], amount: e.target.value } }))}
                    placeholder="Tutar"
                    className="w-24 px-2 py-1 border border-[#E5E5E5] text-xs num text-right"
                  />
                  <button data-testid={`ded-add-${s.supplier_id}`} onClick={() => addDed(s.supplier_id)} className="px-2 py-1 text-[10px] uppercase tracking-widest font-bold bg-[#DC2626] text-white hover:bg-[#0A0A0A]">Kesinti Ekle</button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default function PuantajPage() {
  const now = new Date();
  const [month, setMonth] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`);
  const [company, setCompany] = useState("");
  const [companies, setCompanies] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [defs, setDefs] = useState([]);
  const [rows, setRows] = useState([]); // [{...def, days:[...], total}]
  const [vatPct, setVatPct] = useState(0);
  const [nDays, setNDays] = useState(30);
  const [fillValue, setFillValue] = useState(1);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  // Firma önerileri: companies + mevcut servis tanımları + taşınan personel firmaları
  useEffect(() => {
    (async () => {
      try {
        const [c, s, sd, cp] = await Promise.all([
          api.get("/companies"), api.get("/suppliers"), api.get("/service-defs"), api.get("/customer-personnel"),
        ]);
        const names = new Set([
          ...c.data.map(x => x.name),
          ...sd.data.map(x => x.company_name),
          ...cp.data.map(x => x.company_name),
        ].filter(Boolean));
        const list = [...names].sort((a, b) => a.localeCompare(b, "tr"));
        setCompanies(list);
        setSuppliers(s.data);
        if (!company && list.length > 0) {
          const withDefs = sd.data[0]?.company_name;
          setCompany(withDefs || list[0]);
        }
      } catch (e) { toast.error(errMsg(e)); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadPuantaj = useCallback(async () => {
    if (!company || !month) return;
    try {
      const [{ data }, sd] = await Promise.all([
        api.get("/puantaj", { params: { company, month } }),
        api.get("/service-defs", { params: { company } }),
      ]);
      setDefs(sd.data);
      setRows(data.services);
      setVatPct(data.vat_pct || 0);
      setNDays(data.days_in_month);
      setDirty(false);
    } catch (e) { toast.error(errMsg(e)); }
  }, [company, month]);
  useEffect(() => { loadPuantaj(); }, [loadPuantaj]);

  const days = useMemo(() => dayMeta(month, nDays), [month, nDays]);

  const setCell = (ri, di, val) => {
    const v = Math.max(0, Math.min(99, parseInt(val, 10) || 0));
    setRows(rs => rs.map((r, i) => {
      if (i !== ri) return r;
      const d = [...r.days]; d[di] = v;
      return { ...r, days: d, total: d.reduce((a, x) => a + x, 0) };
    }));
    setDirty(true);
  };

  const fillRow = (ri, weekdaysOnly) => {
    setRows(rs => rs.map((r, i) => {
      if (i !== ri) return r;
      const d = days.map((dm, j) => (weekdaysOnly && dm.weekend ? r.days[j] || 0 : fillValue));
      return { ...r, days: d, total: d.reduce((a, x) => a + x, 0) };
    }));
    setDirty(true);
  };

  const clearRow = (ri) => {
    setRows(rs => rs.map((r, i) => i === ri ? { ...r, days: days.map(() => 0), total: 0 } : r));
    setDirty(true);
  };

  const fillAllWeekdays = () => {
    setRows(rs => rs.map(r => {
      const d = days.map((dm, j) => (dm.weekend ? r.days[j] || 0 : fillValue));
      return { ...r, days: d, total: d.reduce((a, x) => a + x, 0) };
    }));
    setDirty(true);
    toast.info(`Tüm servislerin hafta içi günleri ${fillValue} olarak dolduruldu — Kaydet'e basmayı unutmayın`);
  };

  const copyPrev = async () => {
    try {
      const { data } = await api.post("/puantaj/copy-previous", { company_name: company, month, entries: [] });
      toast.success(`${data.from} ayından ${data.copied} servis puantajı kopyalandı`);
      loadPuantaj();
      setRefreshKey(k => k + 1);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/puantaj", {
        company_name: company, month, vat_pct: vatPct,
        entries: rows.map(r => ({ service_id: r.id, days: r.days })),
      });
      toast.success(`Puantaj kaydedildi — ${data.companies} firma ve ${data.suppliers} tedarikçi hakedişi otomatik güncellendi`);
      setDirty(false);
      setRefreshKey(k => k + 1);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };

  // Özet hesapları
  const totals = useMemo(() => {
    const net = rows.reduce((a, r) => a + r.total * (parseFloat(r.sale_price) || 0), 0);
    const vat = net * (vatPct / 100);
    return { trips: rows.reduce((a, r) => a + r.total, 0), net, vat, gross: net + vat };
  }, [rows, vatPct]);

  const exportSections = useMemo(() => {
    if (rows.length === 0) return [];
    return [{
      name: "Puantaj", heading: `${company} — ${monthTitle(month)} Puantaj`,
      columns: buildExportColumns(nDays, "Fiyat"),
      rows: [
        ...rows.map(r => rowToExport(r.name, r.start_time, r.days, r.total, r.sale_price || 0, (r.total * (r.sale_price || 0)))),
        { name: "GENEL TOPLAM", start_time: "", total: totals.trips, price: "", amount: totals.net },
      ],
    }, {
      name: "Özet", heading: "Fatura Özeti",
      columns: [{ key: "k", label: "Kalem" }, { key: "v", label: "Tutar", format: "money" }],
      rows: [
        { k: "Fatura Edilecek Tutar", v: totals.net },
        { k: `KDV (%${vatPct})`, v: totals.vat },
        { k: "FATURA TOPLAMI", v: totals.gross },
      ],
    }];
  }, [rows, company, month, nDays, totals, vatPct]);

  const inputMonth = (
    <div className="flex items-center gap-2">
      <input
        type="month" data-testid="puantaj-month"
        value={month} onChange={e => setMonth(e.target.value)}
        className="px-2.5 py-2 bg-white border border-[#E5E5E5] text-sm num"
      />
      <select
        data-testid="puantaj-company"
        value={company} onChange={e => setCompany(e.target.value)}
        className="px-2.5 py-2 bg-white border border-[#E5E5E5] text-sm min-w-[180px]"
      >
        <option value="">Firma seçin...</option>
        {companies.map(c => <option key={c} value={c}>{c}</option>)}
      </select>
    </div>
  );

  return (
    <>
      <PageHeader
        title="Servis Puantajları"
        subtitle="Araçların günlük puantajını işleyin — firma ve tedarikçi hakedişleri otomatik hesaplanır."
        testid="puantaj-header"
        actions={inputMonth}
      />

      {company && (
        <>
          <ServiceDefs company={company} month={month} suppliers={suppliers} defs={defs} onChanged={loadPuantaj} />

          {/* Puantaj Grid */}
          {rows.length > 0 && (
            <div className="bg-white border border-[#E5E5E5] border-l-4 border-l-[#0020C2] rounded-md mb-4" data-testid="puantaj-grid">
              <div className="px-5 py-3 border-b border-[#E5E5E5] flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <CalendarCheck2 className="w-4 h-4 text-[#0020C2]" />
                  <span className="font-bold font-heading">{company} — {monthTitle(month)}</span>
                  {dirty && <span className="text-[10px] uppercase tracking-widest font-bold text-[#D97706]">Kaydedilmemiş değişiklik</span>}
                </div>
                <div className="flex items-center gap-1.5 flex-wrap">
                  <label className="flex items-center gap-1 text-[10px] uppercase tracking-widest font-semibold text-neutral-500">
                    Doldurma değeri
                    <input data-testid="puantaj-fill-value" type="number" min="1" max="9" value={fillValue} onChange={e => setFillValue(Math.max(1, parseInt(e.target.value, 10) || 1))} className="w-11 px-1 py-1 border border-[#E5E5E5] num text-center" />
                  </label>
                  <button data-testid="puantaj-fill-weekdays" onClick={fillAllWeekdays} className="flex items-center gap-1 px-2.5 py-1.5 text-[10px] uppercase tracking-widest font-bold border border-[#0020C2] text-[#0020C2] hover:bg-[#0020C2] hover:text-white">
                    <Wand2 className="w-3 h-3" /> Hafta İçini Doldur
                  </button>
                  <button data-testid="puantaj-copy-prev" onClick={copyPrev} className="flex items-center gap-1 px-2.5 py-1.5 text-[10px] uppercase tracking-widest font-bold border border-[#E5E5E5] hover:bg-[#F5F5F5]">
                    <Copy className="w-3 h-3" /> Önceki Aydan Kopyala
                  </button>
                  <button data-testid="puantaj-save" onClick={save} disabled={saving} className="flex items-center gap-1.5 px-4 py-1.5 text-[10px] uppercase tracking-widest font-bold bg-[#0A0A0A] text-white hover:bg-[#0020C2] disabled:opacity-50">
                    {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Kaydet
                  </button>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="text-xs border-collapse w-full">
                  <thead>
                    <tr className="bg-[#FAFAFA]">
                      <th className="sticky left-0 bg-[#FAFAFA] px-2 py-2 text-left text-[10px] uppercase tracking-widest text-neutral-500 font-semibold min-w-[150px] z-10 border-r border-[#E5E5E5]">Servis</th>
                      {days.map(d => (
                        <th key={d.day} className={`px-0.5 py-2 text-center text-[10px] num font-bold w-[26px] ${d.weekend ? "bg-[#FEF3E2] text-[#D97706]" : "text-neutral-500"}`}>{d.day}</th>
                      ))}
                      <th className="px-2 py-2 text-right text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">T</th>
                      <th className="px-2 py-2 text-right text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">Fiyat</th>
                      <th className="px-2 py-2 text-right text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">Tutar</th>
                      <th className="px-1 py-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, ri) => (
                      <tr key={r.id} className="border-t border-[#F5F5F5]" data-testid={`puantaj-row-${r.id}`}>
                        <td className="sticky left-0 bg-white px-2 py-1 font-semibold whitespace-nowrap z-10 border-r border-[#E5E5E5]">
                          {r.name}
                          {r.start_time && <span className="text-neutral-400 font-normal num"> · {r.start_time}</span>}
                        </td>
                        {r.days.map((v, di) => (
                          <td key={di} className={`p-0 ${days[di].weekend ? "bg-[#FFFBF3]" : ""}`}>
                            <input
                              data-testid={`cell-${ri}-${di + 1}`}
                              value={v || ""}
                              onChange={e => setCell(ri, di, e.target.value)}
                              inputMode="numeric"
                              className="w-[26px] h-7 text-center text-xs num border-0 focus:outline-none focus:ring-1 focus:ring-[#0020C2] bg-transparent"
                            />
                          </td>
                        ))}
                        <td className="px-2 py-1 text-right num font-bold" data-testid={`row-total-${ri}`}>{r.total}</td>
                        <td className="px-2 py-1 text-right num text-neutral-500">{formatTL(r.sale_price)}</td>
                        <td className="px-2 py-1 text-right num font-bold">{formatTL(r.total * (r.sale_price || 0))}</td>
                        <td className="px-1 py-1 whitespace-nowrap">
                          <button data-testid={`row-fill-${ri}`} onClick={() => fillRow(ri, true)} title={`Hafta içini ${fillValue} ile doldur`} className="p-1 hover:bg-[#F5F8FF] text-[#0020C2]"><Wand2 className="w-3 h-3" /></button>
                          <button data-testid={`row-clear-${ri}`} onClick={() => clearRow(ri)} title="Satırı temizle" className="p-1 hover:bg-[#FEF2F2] text-[#DC2626]"><Eraser className="w-3 h-3" /></button>
                        </td>
                      </tr>
                    ))}
                    <tr className="border-t-2 border-[#0A0A0A] bg-[#FAFAFA] font-bold">
                      <td className="sticky left-0 bg-[#FAFAFA] px-2 py-1.5 text-[10px] uppercase tracking-widest z-10 border-r border-[#E5E5E5]">Genel Toplam</td>
                      {days.map((d, di) => (
                        <td key={di} className="px-0.5 py-1.5 text-center num text-[10px]">{rows.reduce((a, r) => a + (r.days[di] || 0), 0) || ""}</td>
                      ))}
                      <td className="px-2 py-1.5 text-right num" data-testid="grand-total-trips">{totals.trips}</td>
                      <td></td>
                      <td className="px-2 py-1.5 text-right num" data-testid="grand-total-amount">{formatTL(totals.net)}</td>
                      <td></td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Fatura özeti + export */}
              <div className="px-5 py-3 border-t border-[#E5E5E5] flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-4 text-sm flex-wrap">
                  <span>Fatura Edilecek: <b className="num">{formatTL(totals.net)}</b></span>
                  <label className="flex items-center gap-1.5 text-xs">
                    KDV
                    <select data-testid="puantaj-vat" value={vatPct} onChange={e => { setVatPct(parseFloat(e.target.value)); setDirty(true); }} className="px-1.5 py-1 border border-[#E5E5E5] text-xs num">
                      {VAT_OPTIONS.map(v => <option key={v} value={v}>%{v}</option>)}
                    </select>
                    <span className="num text-neutral-500">{formatTL(totals.vat)}</span>
                  </label>
                  <span className="bg-[#0020C2] text-white px-3 py-1.5">Fatura Toplamı: <b className="num" data-testid="invoice-total">{formatTL(totals.gross)}</b></span>
                </div>
                <ExportButtons
                  testidPrefix="firma-hakedis-export"
                  filename={`firma_hakedis_${company.toLocaleLowerCase("tr-TR").replace(/[^a-z0-9]/g, "_")}_${month}`}
                  title={`FİRMA HAKEDİŞİ — ${company}`}
                  subtitle={`Dönem: ${monthTitle(month)} · ${rows.length} servis · ${totals.trips} sefer · Fatura: ${formatTL(totals.net)} + KDV %${vatPct} = ${formatTL(totals.gross)}`}
                  sections={exportSections}
                />
              </div>
            </div>
          )}

          {rows.length === 0 && defs.length === 0 && (
            <div className="bg-white border border-[#E5E5E5] p-10 text-center text-sm text-neutral-500 mb-4">
              Puantaj girmek için önce yukarıdan servis tanımlayın veya güzergahlardan içe aktarın.
            </div>
          )}
        </>
      )}

      {/* Tedarikçi hakedişleri (ay bazlı, tüm firmalar) */}
      <SupplierSummary month={month} refreshKey={refreshKey} />
    </>
  );
}
